import React from "react"
import { withStyles } from "@material-ui/core/styles"
import MuiAutocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete"
import TextField from "@material-ui/core/TextField"
import SearchIcon from "@material-ui/icons/Search"
import { Typography } from "@material-ui/core"
import { generateSplitterFunction } from "../../../shared/utils"

/**
 * @param {{
 * options,
 * width?,
 * onChange?,
 * value?,
 * optionsInView?,
 * optionHeight?,
 * label?
 * popoverHeightBuffer?
 * getOptionLabel?
 * filterConfig?
 * }} param1
 */
function ComboBox({
  options = [{ title: "" }],
  width = "unset",
  onChange,
  value,
  optionsInView,
  optionHeight,
  label = "",
  popoverHeightBuffer = 3, // small fix to prevent last option getting partly chopped off at the bottom
  getOptionLabel = option => option.title,
  filterConfig,
}) {
  const popoverHeight =
    optionHeight &&
    optionsInView &&
    `${optionHeight * optionsInView + popoverHeightBuffer}px !important` // set popup height

  const StyledAutocomplete = withStyles({
    input: {
      width: "unset", // fix by clearing width limitation
    },
    listbox: {
      maxHeight: popoverHeight,
      padding: 0, // remove extra padding on top and bottom of popup
    },
    option: {
      padding: "8px 16px",
      borderBottom: "1px solid rgba(196, 196, 205, 0.35)",
      "&:nth-child(even)&:not([data-focus='true'])&:not([aria-selected='true'])": {
        backgroundColor: "#FAFAFC",
      },
    },
    paper: {
      borderRadius: 0,
      padding: 0,
    },
    inputRoot: {
      "&[class*='MuiOutlinedInput-root']": {
        paddingTop: 5,
        paddingBottom: 0,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: 0,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2E2E38",
        borderWidth: 1,
      },
    },
    popupIndicatorOpen: {
      transform: "none", // don't flip icon when dropdown is opened (since it's a search icon not an arrow icon)
    },
    popup: {
      zIndex: 10000,
      // if popup appears above the input element, move its shadow to above the input instead
      "&[x-placement='top'] .MuiPaper-elevation1": {
        boxShadow: `
                    0px -2px 1px -1px rgba(0,0,0,0.2), 
                    0px -1px 1px 0px rgba(0,0,0,0.14), 
                    0px -1px 3px 0px rgba(0,0,0,0.12)
        `,
      },
    },
  })(MuiAutocomplete)

  const StyledTextField = withStyles({
    root: {
      width, // fix to remove width limitation
      "& .Mui-focused": {
        color: "#2E2E38",
      },
    },
  })(TextField)

  const paragraphStyle = {
    lineHeight: 1.67, // results in paragraph height of 20px
    fontSize: "0.75rem", // 0.75 rem = 12px (with current theme setup)
  }

  const StyledParagraph = withStyles({
    root: paragraphStyle,
  })(Typography)

  const BoldParagraph = withStyles({
    root: {
      ...paragraphStyle,
      fontWeight: "bold",
    },
  })(Typography)

  const MatchingText = withStyles({
    root: {
      ...paragraphStyle,
      fontWeight: "bold",
      color: "#155CB4",
    },
  })(Typography)

  const splitStringForHighlighting = generateSplitterFunction({
    filterConfig,
    getOptionLabel,
  })

  // adapted from:
  // https://material-ui.com/components/autocomplete/#highlights
  const renderOption = (option, { inputValue }) => {
    const {
      beforeMatch,
      matchingText,
      afterMatch,
    } = splitStringForHighlighting({ option, inputValue })
    return (
      <div>
        <StyledParagraph>{option.heading}</StyledParagraph>
        <BoldParagraph>
          {beforeMatch}
          {matchingText && (
            <MatchingText display="inline">{matchingText}</MatchingText>
          )}
          {afterMatch}
        </BoldParagraph>
      </div>
    )
  }

  return (
    <StyledAutocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      renderInput={params => (
        <>
          <StyledTextField {...params} label={label} variant="outlined" />
        </>
      )}
      renderOption={renderOption}
      onChange={function(e) {
        onChange.apply(null, arguments)
      }}
      value={value}
      popupIcon={<SearchIcon />}
      disableClearable
      filterOptions={createFilterOptions(filterConfig)}
    />
  )
}

export default ComboBox
